/**
 * VideoElement Web Component
 * Reusable video component with viewport-aware play/pause,
 * tab visibility handling, and slideshow integration.
 */
if (!customElements.get("video-element")) {
  customElements.define(
    "video-element",
    class VideoElement extends HTMLElement {
      constructor() {
        super();
        this.observer = null;
        this.resizeObserver = null;
        this.isInViewport = false;
        this.isActiveSlide = true;
        this.isDocumentVisible = true;
        this.hasUserInteracted = false;
        this.hasStartedPlaying = false;
        this.isUserPaused = false;
        this.isProgrammaticPause = false;
        this.currentBreakpoint = null;
        this.isInitialized = false;
        this.videoSourceLoaded = false;
        this.lastSlideChangeTime = 0;

        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
        this.handleSlideChange = this.handleSlideChange.bind(this);
        this.handleStaticClick = this.handleStaticClick.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleIntersection = this.handleIntersection.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handleVideoPlay = this.handleVideoPlay.bind(this);
        this.handleVideoPause = this.handleVideoPause.bind(this);
        this.handleVideoCanPlay = this.handleVideoCanPlay.bind(this);
      }

      /**
       * Called when the element is added to the DOM
       */
      connectedCallback() {
        this.currentBreakpoint = this.getBreakpoint();
        this.initIntersectionObserver();
        this.initResizeObserver();
        this.initEventListeners();
        this.initSlideState();
        this.isInitialized = true;
      }

      /**
       * Called when the element is removed from the DOM
       */
      disconnectedCallback() {
        this.cleanup();
      }

      /**
       * Gets the autoplay setting
       * @returns {boolean}
       */
      get autoplay() {
        return this.dataset.autoplay === "true";
      }

      /**
       * Gets the video element
       * @returns {HTMLVideoElement|null}
       */
      get video() {
        return this.querySelector("video");
      }

      /**
       * Gets the slideshow ID this video belongs to
       * @returns {string|undefined}
       */
      get slideshowId() {
        return this.dataset.slideshowId;
      }

      /**
       * Gets the block ID for this video element
       * @returns {string|undefined}
       */
      get blockId() {
        return this.dataset.blockId;
      }

      /**
       * Gets the desktop video URL
       * @returns {string|undefined}
       */
      get desktopVideoUrl() {
        return this.dataset.videoDesktop;
      }

      /**
       * Gets the mobile video URL (falls back to desktop)
       * @returns {string|undefined}
       */
      get mobileVideoUrl() {
        return this.dataset.videoMobile || this.desktopVideoUrl;
      }

      /**
       * Determines the current breakpoint
       * @returns {string} 'mobile' or 'desktop'
       */
      getBreakpoint() {
        return window.innerWidth >= 750 ? "desktop" : "mobile";
      }

      /**
       * Initializes the video source based on current breakpoint
       * Only called when video first enters viewport (lazy loading)
       * Uses data-src pattern for proper lazy loading
       */
      initVideoSource() {
        const video = this.video;
        if (!video || this.videoSourceLoaded) return;

        this.dataset.loading = "true";
        this.loadVideoSources(video);
        this.videoSourceLoaded = true;
      }

      /**
       * Loads video sources by converting data-src to src
       * Prioritizes MP4 sources over HLS for faster loading
       * @param {HTMLVideoElement} video
       */
      loadVideoSources(video) {
        const sourceClass =
          this.currentBreakpoint === "mobile"
            ? ".video-element__source--mobile"
            : ".video-element__source--desktop";

        let sources = video.querySelectorAll(`source${sourceClass}[data-src]`);

        if (sources.length === 0) {
          sources = video.querySelectorAll("source.video-element__source--desktop[data-src]");
        }

        const sourcesArray = Array.from(sources);

        const mp4Source = sourcesArray.find((s) => {
          const url = s.dataset.src || "";
          const type = s.type || "";
          return type === "video/mp4" || url.includes(".mp4");
        });

        if (mp4Source) {
          video.src = mp4Source.dataset.src;
        } else if (sourcesArray.length > 0) {
          video.src = sourcesArray[0].dataset.src;
        }

        video.preload = "auto";
        video.load();
      }

      /**
       * Sets up the IntersectionObserver for viewport detection
       */
      initIntersectionObserver() {
        this.observer = new IntersectionObserver(this.handleIntersection, {
          threshold: 0,
          rootMargin: "200px",
        });
        this.observer.observe(this);
      }

      /**
       * Sets up the ResizeObserver for breakpoint detection
       */
      initResizeObserver() {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(document.documentElement);
      }

      /**
       * Gets the controls setting
       * @returns {boolean}
       */
      get controls() {
        return this.dataset.controls === "true";
      }

      /**
       * Initializes all event listeners
       */
      initEventListeners() {
        document.addEventListener("visibilitychange", this.handleVisibilityChange);
        document.addEventListener("slideshow:slide-change", this.handleSlideChange);

        if (!this.autoplay) {
          /**
           * Listen for Flickity's staticClick event which fires for actual clicks
           * (not drags). This allows dragging to still work for slide navigation.
           */
          document.addEventListener("slideshow:static-click", this.handleStaticClick);

          /**
           * Fallback click handler for non-slideshow contexts.
           */
          this.addEventListener("click", this.handleClick);
        }

        const video = this.video;
        if (video) {
          video.addEventListener("play", this.handleVideoPlay);
          video.addEventListener("pause", this.handleVideoPause);
          video.addEventListener("canplay", this.handleVideoCanPlay);
        }
      }

      /**
       * Initializes the slide state for slideshow integration
       */
      initSlideState() {
        const slide = this.closest(".slideshow-slide");
        if (slide) {
          this.isActiveSlide = slide.classList.contains("is-selected");

          if (!this.isActiveSlide) {
            const isFirstSlide = slide === slide.parentElement?.firstElementChild;
            this.isActiveSlide = isFirstSlide;
          }
        }
      }

      /**
       * Handles intersection changes (viewport visibility)
       * @param {IntersectionObserverEntry[]} entries
       */
      handleIntersection(entries) {
        entries.forEach((entry) => {
          this.isInViewport = entry.isIntersecting;

          if (entry.isIntersecting && !this.videoSourceLoaded) {
            this.initVideoSource();
          }

          this.updatePlaybackState();
        });
      }

      /**
       * Handles resize events for breakpoint switching
       */
      handleResize() {
        const newBreakpoint = this.getBreakpoint();
        if (newBreakpoint !== this.currentBreakpoint) {
          this.currentBreakpoint = newBreakpoint;
          this.switchVideoSource();
        }
      }

      /**
       * Handles document visibility changes (tab switching)
       */
      handleVisibilityChange() {
        this.isDocumentVisible = !document.hidden;
        this.updatePlaybackState();
      }

      /**
       * Handles slideshow slide change events
       * @param {CustomEvent} event
       */
      handleSlideChange(event) {
        const { id, index } = event.detail;
        const now = Date.now();

        /**
         * Debounce rapid slide change events.
         * Flickity can fire multiple change events during transitions.
         * We only process events that are at least 100ms apart.
         */
        if (now - this.lastSlideChangeTime < 100) {
          return;
        }
        this.lastSlideChangeTime = now;

        if (this.slideshowId && id !== this.slideshowId) {
          return;
        }

        const slide = this.closest(".slideshow-slide");
        if (!slide) {
          this.isActiveSlide = true;
          return;
        }

        const slides = Array.from(slide.parentElement?.children || []).filter(
          (el) => el.classList.contains("slideshow-slide")
        );
        const slideIndex = slides.indexOf(slide);
        const wasActive = this.isActiveSlide;
        this.isActiveSlide = slideIndex === index;

        /**
         * Reset user interaction state when navigating away from slide.
         * This prevents auto-resume when returning to a non-autoplay video.
         */
        if (wasActive && !this.isActiveSlide && !this.autoplay) {
          this.hasUserInteracted = false;
        }

        /**
         * Flickity clones slides for wrapAround, so we need to pause ALL
         * video elements in the slideshow, not just our instance.
         * This ensures cloned videos are also paused.
         */
        if (!this.isActiveSlide) {
          this.pauseAllSlideshowVideos();
        }

        this.updatePlaybackState();
      }

      /**
       * Pauses all video elements in the parent slideshow.
       * Needed because Flickity clones slides for wrapAround.
       */
      pauseAllSlideshowVideos() {
        const slideshow = this.closest('.slideshow');
        if (!slideshow) return;

        const videos = slideshow.querySelectorAll('video');
        videos.forEach((video) => {
          if (!video.paused) {
            video.pause();
          }
        });
      }

      /**
       * Handles Flickity's staticClick event for slideshow contexts.
       * staticClick fires for actual clicks, not drags.
       * @param {CustomEvent} event
       */
      handleStaticClick(event) {
        const { id, clickedElement } = event.detail;

        if (this.slideshowId && id !== this.slideshowId) {
          return;
        }

        /**
         * When native controls are enabled and video has started,
         * let native controls handle play/pause to avoid double-toggle.
         */
        if (this.controls && this.hasStartedPlaying) {
          return;
        }

        /**
         * Check if the click was on this video element or its children.
         */
        if (!this.contains(clickedElement)) {
          return;
        }

        const isValidTarget = clickedElement === this ||
          clickedElement.classList.contains('video-element__video') ||
          clickedElement.classList.contains('video-element__poster') ||
          clickedElement.closest('.video-element__poster');

        if (!isValidTarget) {
          return;
        }

        this.togglePlayback();
      }

      /**
       * Handles click events for manual playback (non-slideshow contexts)
       * @param {Event} event
       */
      handleClick(event) {
        /**
         * If inside a slideshow, let staticClick handle it.
         */
        if (this.slideshowId) {
          return;
        }

        if (this.autoplay) return;

        /**
         * When native controls are enabled and video has started,
         * let native controls handle play/pause to avoid double-toggle.
         */
        if (this.controls && this.hasStartedPlaying) {
          return;
        }

        const clickTarget = event.target;
        const isValidTarget = clickTarget === this ||
          clickTarget.classList.contains('video-element__video') ||
          clickTarget.classList.contains('video-element__poster') ||
          clickTarget.closest('.video-element__poster');

        if (!isValidTarget) {
          return;
        }

        this.togglePlayback();
      }

      /**
       * Toggles video playback state
       */
      togglePlayback() {
        this.hasUserInteracted = true;

        const video = this.video;
        if (video && !video.paused) {
          this.isUserPaused = true;
          this.pause();
        } else {
          this.isUserPaused = false;
          this.play();
        }
      }

      /**
       * Handles video play event
       */
      handleVideoPlay() {
        this.dataset.playing = "true";
        this.dataset.loading = "false";
        this.isUserPaused = false;
        if (!this.hasStartedPlaying) {
          this.hasStartedPlaying = true;
          this.dataset.started = "true";
        }
        this.dispatchEvent(
          new CustomEvent("video-element:play", { bubbles: true })
        );
      }

      /**
       * Handles video pause event
       */
      handleVideoPause() {
        this.dataset.playing = "false";

        /**
         * If pause was NOT triggered programmatically (by our code),
         * it means user paused via native controls - remember this state.
         */
        if (!this.isProgrammaticPause) {
          this.isUserPaused = true;
        }
        this.isProgrammaticPause = false;

        this.dispatchEvent(
          new CustomEvent("video-element:pause", { bubbles: true })
        );
      }

      /**
       * Handles video canplay event
       */
      handleVideoCanPlay() {
        this.dataset.loading = "false";
      }

      /**
       * Switches video source based on current breakpoint
       */
      switchVideoSource() {
        const video = this.video;
        if (!video) return;

        const wasPlaying = !video.paused;
        const currentTime = video.currentTime;
        const targetUrl =
          this.currentBreakpoint === "mobile"
            ? this.mobileVideoUrl
            : this.desktopVideoUrl;

        if (targetUrl && video.src !== targetUrl) {
          this.dataset.loading = "true";
          video.src = targetUrl;
          video.load();

          video.addEventListener(
            "loadeddata",
            () => {
              video.currentTime = currentTime;
              if (wasPlaying && this.shouldPlay()) {
                this.play();
              }
            },
            { once: true }
          );
        }
      }

      /**
       * Determines if the video should be playing
       * @returns {boolean}
       */
      shouldPlay() {
        if (!this.isDocumentVisible) return false;
        if (!this.isInViewport) return false;
        if (!this.isActiveSlide) return false;
        if (!this.autoplay && !this.hasUserInteracted) return false;
        if (this.isUserPaused) return false;
        return true;
      }

      /**
       * Updates playback state based on all conditions
       */
      updatePlaybackState() {
        if (!this.isInitialized) return;

        if (this.shouldPlay()) {
          this.play();
        } else {
          this.pause();
        }
      }

      /**
       * Starts video playback with error handling
       */
      play() {
        const video = this.video;
        if (!video || !video.paused) return;

        if (!this.videoSourceLoaded) {
          this.initVideoSource();
          this.dataset.loading = "true";
          video.addEventListener(
            "canplay",
            () => {
              if (this.shouldPlay()) {
                this.playVideo();
              }
            },
            { once: true }
          );
          return;
        }

        this.playVideo();
      }

      /**
       * Actually plays the video with error handling
       */
      playVideo() {
        const video = this.video;
        if (!video || !video.paused) return;

        this.dataset.loading = "true";
        const playPromise = video.play();

        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            this.dataset.loading = "false";
            if (error.name !== "AbortError") {
              console.warn("VideoElement: Playback failed", error);
            }
          });
        }
      }

      /**
       * Pauses video playback
       */
      pause() {
        const video = this.video;
        if (!video || video.paused) return;

        this.isProgrammaticPause = true;
        video.pause();
      }

      /**
       * Resets video to the beginning
       */
      reset() {
        const video = this.video;
        if (!video) return;

        video.currentTime = 0;
      }

      /**
       * Cleans up all observers and event listeners
       */
      cleanup() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }

        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }

        document.removeEventListener("visibilitychange", this.handleVisibilityChange);
        document.removeEventListener("slideshow:slide-change", this.handleSlideChange);
        document.removeEventListener("slideshow:static-click", this.handleStaticClick);
        this.removeEventListener("click", this.handleClick);

        const video = this.video;
        if (video) {
          video.removeEventListener("play", this.handleVideoPlay);
          video.removeEventListener("pause", this.handleVideoPause);
          video.removeEventListener("canplay", this.handleVideoCanPlay);
        }
      }
    }
  );
}
